import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    SoundType,
} from '@denishussar/html5-game-engine';
import { GameCollectableTypeEnum, GameModel } from '../../Models/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import {
    IUICollectableItemControllerOptions,
    UICollectableItemController,
} from './UICollectableItemController';
import { gsap } from 'gsap';
import { Sounds } from '../../Constants/Sounds';
import { SoundModel } from '../../Models/SoundModel';
import { Route } from '../../Utils/Helpers';

export class UICollectableController extends AnimationBaseController {
    $model: GameModel;

    private items = {};

    public constructor(
        @Inject private _options: any,
        @Inject private _gameLogic: GameLogic,
        @Inject private soundLevels: SoundModel
    ) {
        super('UICollectableController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const sfx = [
            Sounds.USE_BANDITS,
            Sounds.USE_DEGEN,
            Sounds.USE_DPS,
            Sounds.USE_UNICORN,
        ];

        const sounds = [];
        sfx.forEach(sound => {
            sounds.push({ id: sound, type: SoundType.SFX });
        });
        return <IAnimationControllerConfiguration>{
            childControllers: {
                item_horse: {
                    instanceName: 'item1',
                    controller: UICollectableItemController,
                    model: this.$model.collectablesState,
                    options: <IUICollectableItemControllerOptions>{
                        type: GameCollectableTypeEnum.horse,
                        index: 0,
                        onClick: (
                            type: GameCollectableTypeEnum,
                            index: number
                        ) => this.onCollectableClick(type, index),
                    },
                },
                item_bat: {
                    instanceName: 'item2',
                    controller: UICollectableItemController,
                    model: this.$model.collectablesState,
                    options: <IUICollectableItemControllerOptions>{
                        type: GameCollectableTypeEnum.bat,
                        index: 1,
                        onClick: (
                            type: GameCollectableTypeEnum,
                            index: number
                        ) => this.onCollectableClick(type, index),
                    },
                },
                item_crypto: {
                    instanceName: 'item3',
                    controller: UICollectableItemController,
                    model: this.$model.collectablesState,
                    options: <IUICollectableItemControllerOptions>{
                        type: GameCollectableTypeEnum.crypto,
                        index: 2,
                        onClick: (
                            type: GameCollectableTypeEnum,
                            index: number
                        ) => this.onCollectableClick(type, index),
                    },
                },
                item_gun: {
                    instanceName: 'item4',
                    controller: UICollectableItemController,
                    model: this.$model.collectablesState,
                    options: <IUICollectableItemControllerOptions>{
                        type: GameCollectableTypeEnum.gun,
                        index: 3,
                        onClick: (
                            type: GameCollectableTypeEnum,
                            index: number
                        ) => this.onCollectableClick(type, index),
                    },
                },
            },
            modelEvents: {
                useCollectable: (type: GameCollectableTypeEnum) => {
                    this.updateItems();
                    this.useCollectable(type);
                },
                takeCollectable: (type: GameCollectableTypeEnum) => {
                    this.updateItems();
                    this.takeCollectable(type);
                },
            },
            modelWatch: {
                collectablesObstacle: obstacles => {
                    console.log('changes obstacle collectable');
                    Object.keys(obstacles).forEach(key => {
                        this.items[key].shine = obstacles[key] ? true : false;
                        this.items[key].setState(this.items[key].active);
                    });
                },
            },
        };
    }

    protected onInitialize(): void {
        this.updateItems(true);
        this.display.scale.set(2);
        const horse = this.$childControllers.get(
            'item_horse'
        ) as UICollectableController;
        Object.assign(
            this.$model.spineResources,
            horse.resourcesLoader.loader.resources
        );

        this.items[GameCollectableTypeEnum.crypto] =
            this.$childControllers.get('item_crypto');
        this.items[GameCollectableTypeEnum.bat] =
            this.$childControllers.get('item_bat');
        this.items[GameCollectableTypeEnum.gun] =
            this.$childControllers.get('item_gun');
        this.items[GameCollectableTypeEnum.horse] =
            this.$childControllers.get('item_horse');
    }

    protected onResize(width: number, height: number, scale: number) {}

    onUpdate() {
        const obstacles = this.$model.collectablesObstacle;
        Object.keys(obstacles).forEach(key => {
            if (this.items[key]) {
                this.items[key].setActive(
                    this.items[key].active,
                    obstacles[key] ? true : false
                );
            }
        });
    }

    private onCollectableClick(
        type: GameCollectableTypeEnum,
        index: number
    ): void {
        if (this.$model.collectablesState[type])
            switch (type) {
                case GameCollectableTypeEnum.horse: {
                    this.soundManager.play(
                        Sounds.USE_UNICORN,
                        false,
                        SoundModel.UI
                    );
                    break;
                }

                case GameCollectableTypeEnum.bat: {
                    this.soundManager.play(
                        Sounds.USE_DEGEN,
                        false,
                        SoundModel.UI
                    );
                    break;
                }

                case GameCollectableTypeEnum.crypto: {
                    this.soundManager.play(
                        Sounds.USE_DPS,
                        false,
                        SoundModel.UI
                    );
                    break;
                }

                case GameCollectableTypeEnum.gun: {
                    this.soundManager.play(
                        Sounds.USE_BANDITS,
                        false,
                        SoundModel.UI
                    );
                    break;
                }
            }
        this._gameLogic.useCollectable(type);
    }

    private getItems(): UICollectableItemController[] {
        const values = Object.values(GameCollectableTypeEnum);
        return values
            .splice(1, values.length)
            .map(value =>
                this.$childControllers.get<UICollectableItemController>(
                    `item_${value}`
                )
            );
    }

    private getItem(
        type: GameCollectableTypeEnum
    ): UICollectableItemController {
        return this.getItems().find(item => item.getType() === type);
    }

    private async useCollectable(
        type: GameCollectableTypeEnum,
        duration: number = 0.15
    ): Promise<void> {
        // return new Promise(resolve => {
        //     gsap.to(this.getItem(type).display.scale, { duration: duration, x: 0, y: 0, onComplete: resolve });
        // });
        this.$model.collectablesState[type] = false;
        return new Promise(() => {});
    }

    private async takeCollectable(
        type: GameCollectableTypeEnum,
        duration: number = 0.15
    ): Promise<void> {
        // return new Promise(resolve => {
        //     gsap.to(this.getItem(type).display.scale, { duration: duration, x: 1, y: 1, onComplete: resolve });
        // });
    }

    private updateItems(withScale: boolean = false): void {
        // const values = Object.values(GameCollectableTypeEnum);
        // const items = values.splice(1, values.length).map(value => this.$childControllers.get<UICollectableItemController>(`item_${value}`));

        // let itemWidth = 364;

        // if (withScale) {
        //     items.forEach(item => {
        //         item.display.scale.set(0);
        //     });
        // }

        this.$model.collectablesState.forEach((state: boolean, key: string) => {
            const controller = this.$childControllers.get(
                'item_' + key
            ) as UICollectableItemController;
            controller.setActive(state);
        });

        // const activeCollectables = this._gameLogic.getActiveCollectables();
        // const totalWidth = activeCollectables.length * itemWidth;

        // activeCollectables.forEach((type, index) => {
        //     const item = items.find(item => item.getType() === type);

        //     if (withScale) {
        //         item.display.scale.set(1);
        //     }

        //     item.display.x = -totalWidth / 2 + index * itemWidth + itemWidth / 2;
        // });
    }
}
